import { Transaction } from 'components/TransactionAllocator';
import { ConsiderationTypeHoldingClassTypes } from 'further-types/lp/realisation';
import { CurrencyRateDetails } from 'further-types/lp/transaction';
import { Vehicle } from 'further-types/lp/vehicle';
import { CalendarDay, calculateLcyValue } from 'further-ui/utils';
import { compact } from 'lodash';
import {
  Considerations,
  HoldingClassInvestmentsConsiderationValues,
  HoldingClassRealisations,
} from '../../types';

type ConsiderationAmounts = {
  receivedConsiderationPerUnit?: number;
  receivedConsiderationTotal?: number;
  escrowConsiderationPerUnit?: number;
  escrowConsiderationTotal?: number;
  earnoutConsiderationPerUnit?: number;
  earnoutConsiderationTotal?: number;
  withholdingTaxPayable?: number;
};

const convertConsiderations = (consideration?: ConsiderationAmounts) => ({
  receivedConsiderationPerUnitTcy:
    consideration?.receivedConsiderationPerUnit ?? 0,
  receivedConsiderationTotalTcy: consideration?.receivedConsiderationTotal ?? 0,
  escrowConsiderationPerUnitTcy: consideration?.escrowConsiderationPerUnit ?? 0,
  escrowConsiderationTotalTcy: consideration?.escrowConsiderationTotal ?? 0,
  earnoutConsiderationPerUnitTcy:
    consideration?.earnoutConsiderationPerUnit ?? 0,
  earnoutConsiderationTotalTcy: consideration?.earnoutConsiderationTotal ?? 0,
  withholdingTaxPayableTcy: consideration?.withholdingTaxPayable ?? 0,
});

type BuildRealisationPayloadParams = {
  selectedVehicle: Vehicle;
  assetId: string;
  ledgerDate: CalendarDay;

  holdingClassRealisation: HoldingClassRealisations;
  holdingClassInvestmentUnwindedGains: Array<{
    holdingClassId: string;
    investmentId: string;
    unwindedGainsLcy: number;
    unwindedGainsTcy: number;
  }>;
  realisationType: ConsiderationTypeHoldingClassTypes;
  consideration: Considerations;
  cashConsideration: Considerations[number];
  finalTransactions: Array<Transaction>;
  currencyRate: CurrencyRateDetails | undefined;
  holdingClassInvestmentConsiderationValues: Array<HoldingClassInvestmentsConsiderationValues>;
  saleOfInvestment: Array<{
    investmentId: string;
    holdingClassId: string;
    investmentDate: CalendarDay;
    investmentSaleUnwindLcy: number;
    investmentSaleUnwindTcy: number;
  }>;
};

const mapHoldingClassRealisationAllocations = (
  holdingClassRealisation: HoldingClassRealisations,
  holdingClassInvestmentConsiderationValues: Array<HoldingClassInvestmentsConsiderationValues>,
  currencyRate: CurrencyRateDetails | undefined,
) =>
  holdingClassRealisation.map((holdingClass) => ({
    holdingClassId: holdingClass.holdingClassId,
    unitsSold: holdingClass.unitsSold ?? 0,
    partialRealisationAllocationLogic:
      holdingClass.partialRealisationAllocationLogic,
    cashConsiderationAmounts: holdingClass.cashConsiderationAmounts,
    unitsAllocationPerInvestment: compact(
      holdingClass.unitsAllocationPerInvestment?.map((allocation) => {
        const investmentConsiderationValue =
          holdingClassInvestmentConsiderationValues.find(
            (investment) =>
              investment.investmentId === allocation.investmentId &&
              investment.holdingClassId === holdingClass.holdingClassId,
          );

        if (!investmentConsiderationValue) {
          return null;
        }
        return {
          investmentId: allocation.investmentId,
          investmentCurrency: allocation.currency,
          allocatedUnits: allocation.allocatedUnits,
          receivedConsiderationTotalTcy:
            investmentConsiderationValue?.receivedConsiderationTotal ?? 0,
          escrowConsiderationTotalTcy:
            investmentConsiderationValue?.escrowConsiderationTotal ?? 0,
          earnoutConsiderationTotalTcy:
            investmentConsiderationValue?.earnoutConsiderationTotal ?? 0,
          withholdingTaxPayableTcy:
            investmentConsiderationValue?.withholdingTaxPayable ?? 0,
          receivedConsiderationTotalLcy: calculateLcyValue(
            investmentConsiderationValue?.receivedConsiderationTotal ?? 0,
            currencyRate,
          ),
          escrowConsiderationTotalLcy: calculateLcyValue(
            investmentConsiderationValue?.escrowConsiderationTotal ?? 0,
            currencyRate,
          ),
          earnoutConsiderationTotalLcy: calculateLcyValue(
            investmentConsiderationValue?.earnoutConsiderationTotal ?? 0,
            currencyRate,
          ),
          withholdingTaxPayableLcy: calculateLcyValue(
            investmentConsiderationValue?.withholdingTaxPayable ?? 0,
            currencyRate,
          ),
        };
      }) ?? [],
    ),
  }));

const buildRealisationPayload = ({
  selectedVehicle,
  assetId,
  ledgerDate,
  currencyRate,
  holdingClassRealisation,
  realisationType,
  consideration,
  cashConsideration,
  finalTransactions,
  holdingClassInvestmentUnwindedGains,
  holdingClassInvestmentConsiderationValues,
  saleOfInvestment,
}: BuildRealisationPayloadParams) => ({
  vehicleId: selectedVehicle._id,
  assetId,
  ledgerDate,
  currencyRate,
  holdingClassRealisationAllocations: mapHoldingClassRealisationAllocations(
    holdingClassRealisation,
    holdingClassInvestmentConsiderationValues,
    currencyRate,
  ),
  realisationType,
  considerationEntries: consideration.map((consideration) => ({
    currencyCode: consideration.currency,
    considerationType: consideration.considerationType,
    considerationAllocationLogic: consideration.considerationAllocationLogic,
    proRataConsiderationAmounts: convertConsiderations(
      consideration.proRataConsideration,
    ),
    perHoldingClassConsiderationAmounts: holdingClassRealisation.map(
      (holdingClass) => ({
        holdingClassId: holdingClass.holdingClassId,
        ...convertConsiderations(holdingClass.cashConsiderationAmounts),
      }),
    ),
    investorAllocations:
      consideration.investorAllocations?.map((investorAllocation) => ({
        investorLabel: investorAllocation.investorName,
        commitmentId: investorAllocation.commitmentId,
        investmentId: investorAllocation.investmentId,
        holdingClassId: investorAllocation.holdingClassId,
        allocationType: investorAllocation.allocationType,
        allocationPercentage: investorAllocation.allocationPercentage,
        originalAllocationPercentage:
          investorAllocation.originalAllocationPercentage,
        unitsAllocated: investorAllocation.unitsAllocated,
        receivedConsiderationTcy: investorAllocation.receivedConsideration,
        earnoutConsiderationTcy: investorAllocation.earnoutConsideration,
        escrowConsiderationTcy: investorAllocation.escrowConsideration,
        withholdingTaxPayableTcy: investorAllocation.withholdingTaxPayable,
        withholdingTaxExempt: investorAllocation.withholdingTaxExempt ?? false,
      })) ?? [],
  })),
  currencyCode: cashConsideration?.currency,
  holdingClassInvestmentUnwindedGains,
  saleOfInvestment,
  transactions: finalTransactions.filter(
    (t) => t.amountExVatTcy !== 0 || t.amountExVatLcy !== 0,
  ),
});

export default buildRealisationPayload;
